import {
  createContext,
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { ordersApi, productsApi } from '@/lib/apiServices';
import { apiClient } from '@/lib/apiClient';
import { getCookie, setCookie, deleteCookie } from '@/lib/cookies';
import { getSharedCookieDomain } from '@/lib/subdomain';
import { AuthContext } from '@/features/auth/context/AuthContext';

export const CartContext = createContext(null);

const GUEST_CART_COOKIE = 'guest_cart';

const readGuestCookie = () => {
  try {
    const raw = getCookie(GUEST_CART_COOKIE);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Unable to read guest cart:', error);
    return [];
  }
};

const writeGuestCookie = (items) => {
  try {
    setCookie(GUEST_CART_COOKIE, JSON.stringify(items), {
      domain: getSharedCookieDomain(),
    });
    return true;
  } catch (error) {
    console.warn('Unable to save guest cart:', error);
    return false;
  }
};

const clearGuestCookie = () => {
  try {
    deleteCookie(GUEST_CART_COOKIE, { domain: getSharedCookieDomain() });
    return true;
  } catch (error) {
    console.warn('Unable to clear guest cart:', error);
    return false;
  }
};

export const CartProvider = ({ children }) => {
  const { user, activeRole, loading: authLoading } = useContext(AuthContext);
  const authStateRef = useRef({ user, authLoading });
  const authReadyRef = useRef(null);

  const canShop = !user || activeRole === 'customer';

  const [cart, setCart] = useState([]);
  const [toast, setToast] = useState(null);
  const [cartWarning, setCartWarning] = useState('');

  const productCache = useRef(new Map());

  useLayoutEffect(() => {
    authStateRef.current = { user, authLoading };
  }, [authLoading, user]);

  useEffect(() => {
    if (!authLoading && authReadyRef.current) {
      authReadyRef.current.resolve();
      authReadyRef.current = null;
    }
  }, [authLoading, user]);

  const waitForAuth = useCallback(async () => {
    if (authStateRef.current.authLoading) {
      if (!authReadyRef.current) {
        let resolve;
        const promise = new Promise((done) => {
          resolve = done;
        });
        authReadyRef.current = { promise, resolve };
      }
      await authReadyRef.current.promise;
    }
    return authStateRef.current;
  }, []);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2000);
  };

  const hydrateItems = useCallback(async (rawItems) => {
    const results = await Promise.all(
        rawItems.map(async (raw) => {
          const productId = raw.product_id ?? raw.id;
          let product = productCache.current.get(productId);

          if (!product) {
            try {
              const { data } = await productsApi.get(productId);
              product = data;
              productCache.current.set(productId, product);
            } catch (error) {
              console.warn('Cart: product no longer available', productId, error);
              return null;
            }
          }

          return {
            ...product,
            quantity: raw.quantity,
            discount_percent: Number(raw.discount_percent) || 0,
            package_name: raw.package_name || null
          };
        })
    );

    return results.filter(Boolean);
  }, []);

  const loadGuestCart = useCallback(async () => {
    const guestItems = readGuestCookie();
    if (guestItems.length === 0) {
      setCart([]);
      return [];
    }
    const items = await hydrateItems(guestItems);
    setCart(items);
    return items;
  }, [hydrateItems]);

  const loadServerCart = useCallback(async () => {
    try {
      const { data } = await ordersApi.getBasket();
      const items = (data.items ?? []).map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        discount_percent: item.discount_percent,
        package_name: item.package_name,
      }));
      const hydratedItems = await hydrateItems(items);
      setCart(hydratedItems);
      return hydratedItems;
    } catch (error) {
      console.error('Failed to load basket:', error);
      return null;
    }
  }, [hydrateItems]);

  const mergeGuestCartIntoServer = useCallback(async () => {
    const guestItems = readGuestCookie();
    if (guestItems.length === 0) {
      setCartWarning('');
      return;
    }

    const failedItems = [];
    for (const item of guestItems) {
      try {
        await apiClient.POST(`/api/v1/customer/cart/items/${item.product_id}`, {
          body: {
            quantity: item.quantity,
            discount_percent: item.discount_percent || 0,
            package_name: item.package_name || null,
          },
        });
      } catch (error) {
        console.warn('Failed to merge guest cart item:', item.product_id, error);
        failedItems.push(item);
      }
    }
    if (failedItems.length > 0) {
      const saved = writeGuestCookie(failedItems);
      setCartWarning(
        saved
          ? 'برخی کالاهای سبد مهمان به سبد حساب شما منتقل نشدند. اتصال را بررسی کنید؛ کالاها برای تلاش دوباره نگه داشته شدند.'
          : 'برخی کالاهای سبد مهمان منتقل نشدند و ذخیره دوباره آن‌ها هم ممکن نبود. این صفحه را نبندید و پس از بررسی اتصال دوباره تلاش کنید.',
      );
    } else {
      const cleared = clearGuestCookie();
      setCartWarning(
        cleared
          ? ''
          : 'انتقال سبد انجام شد اما پاک‌کردن سبد مهمان ناموفق بود. پیش از تلاش دوباره، وضعیت سبد را بررسی کنید.',
      );
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (user && activeRole === 'customer') {
      (async () => {
        await mergeGuestCartIntoServer();
        await loadServerCart();
      })();
    } else if (!user) {
      loadGuestCart();
    } else {
      setCart([]);
    }
  }, [user, authLoading, activeRole]);

  const addToCart = async (product, quantity = 1) => {
    const authState = await waitForAuth();
    const requestedQuantity = Math.max(1, Number(quantity) || 1);
    productCache.current.set(product.id, product);

    if (authState.user) {
      try {
        const { data } = await apiClient.POST(`/api/v1/customer/cart/items/${product.id}`, {
          body: {
            quantity: requestedQuantity,
            discount_percent: product.discount_percent || 0,
            package_name: product.package_name || null,
          },
        });
        const items = (data.items ?? []).map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          discount_percent: item.discount_percent,
          package_name: item.package_name,
        }));
        setCart(await hydrateItems(items));
        showToast('به سبد خرید اضافه شد');
      } catch (error) {
        console.error('Add to basket failed:', error);
        throw error;
      }
      return;
    } else if (user && activeRole !== 'customer') {
      throw new Error('فقط مشتریان می‌توانند خرید کنند');
    }

    setCartWarning('');
    setCart((prev) => {
      const existingItem = prev.find((item) => item.id === product.id);
      const stock = Number(product.stock_quantity);
      let next;

      if (existingItem) {
        const newQuantity = existingItem.quantity + requestedQuantity;
        const finalQuantity = Number.isFinite(stock) && stock >= 0 ? Math.min(newQuantity, stock) : newQuantity;
        next = prev.map((item) => item.id === product.id ? { ...item, quantity: finalQuantity } : item);
      } else {
        const finalQuantity = Number.isFinite(stock) && stock >= 0 ? Math.min(requestedQuantity, stock) : requestedQuantity;
        next = [...prev, { ...product, quantity: finalQuantity }];
      }

      writeGuestCookie(
          next.map((item) => ({
            product_id: item.id,
            quantity: item.quantity,
            discount_percent: item.discount_percent || 0,
            package_name: item.package_name || null
          }))
      );
      return next;
    });

    showToast('به سبد خرید اضافه شد');
  };

  const removeFromCart = async (productId) => {
    const itemToRemove = cart.find((item) => item.id === productId);
    if (!itemToRemove) return;

    const targetPackage = itemToRemove.package_name;

    if (user && activeRole === 'customer') {
      try {
        if (targetPackage) {
          const itemsToRemove = cart.filter((i) => i.package_name === targetPackage);
          for (const item of itemsToRemove) {
            await ordersApi.removeBasketItem(item.id);
          }
          await loadServerCart();
        } else {
          const { data } = await ordersApi.removeBasketItem(productId);
          const items = (data.items ?? []).map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            discount_percent: item.discount_percent,
            package_name: item.package_name,
          }));
          setCart(await hydrateItems(items));
        }
      } catch (error) {
        console.error('Remove from basket failed:', error);
      }
      return;
    }

    setCart((prev) => {
      let next;
      if (targetPackage) next = prev.filter((item) => item.package_name !== targetPackage);
      else next = prev.filter((item) => item.id !== productId);

      writeGuestCookie(
          next.map((item) => ({
            product_id: item.id,
            quantity: item.quantity,
            discount_percent: item.discount_percent || 0,
            package_name: item.package_name || null
          }))
      );
      return next;
    });
  };

  const updateQuantity = async (id, quantity) => {
    const requestedQuantity = Math.max(1, Number(quantity) || 1);

    if (user && activeRole === 'customer') {
      try {
        const { data } = await ordersApi.updateBasketItem(id, { quantity: requestedQuantity });
        const items = (data.items ?? []).map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          discount_percent: item.discount_percent,
          package_name: item.package_name,
        }));
        setCart(await hydrateItems(items));
      } catch (error) {
        console.error('Update basket item failed:', error);
      }
      return;
    }

    setCart((prev) => {
      const next = prev.map((item) => {
        if (item.id !== id) return item;
        const stock = Number(item.stock_quantity);
        const finalQuantity = Number.isFinite(stock) && stock >= 0 ? Math.min(requestedQuantity, stock) : requestedQuantity;
        return { ...item, quantity: finalQuantity };
      });
      writeGuestCookie(
          next.map((item) => ({
            product_id: item.id,
            quantity: item.quantity,
            discount_percent: item.discount_percent || 0,
            package_name: item.package_name || null
          }))
      );
      return next;
    });
  };

  const clearCart = async () => {
    clearGuestCookie();
    if (user && activeRole === 'customer') {
      try {
        await ordersApi.clearBasket();
      } catch (error) {
        console.error('Clear basket failed:', error);
      }
    }
    setCart([]);
  };

  const refreshCart = useCallback(async () => {
    const authState = await waitForAuth();
    if (authState.user) {
      return loadServerCart();
    }
    return loadGuestCart();
  }, [loadGuestCart, loadServerCart, waitForAuth]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartWarning,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        refreshCart,
        toast,
        canShop,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};